import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import DotTrickGenerator from './components/DotTrickGenerator';
import Header from './components/Header';
import Footer from './components/Footer';
import { Container, Box, MenuItem, FormControl, Select, InputLabel } from '@mui/material';

// Create a custom dark theme with fluent design
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4cc2ff', // Bright blue
    },
    secondary: {
      main: '#ff4081', // Pink
    },
    background: {
      default: '#121212',
      paper: 'rgba(18, 18, 18, 0.8)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 500,
    },
    h4: {
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.25)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #4cc2ff 30%, #34b3f1 90%)',
        },
        containedSecondary: {
          background: 'linear-gradient(45deg, #ff4081 30%, #f73378 90%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          background: 'rgba(30, 30, 30, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'box-shadow 0.3s ease',
            '&:hover': {
              boxShadow: '0 0 8px rgba(76, 194, 255, 0.5)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 12px rgba(76, 194, 255, 0.8)',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          background: 'rgba(45, 45, 45, 0.5)',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
            background: 'rgba(55, 55, 55, 0.6)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
});

// Background image options
const backgrounds = [
  {
    id: 'tech1',
    url: 'https://w.wallhaven.cc/full/k8/wallhaven-k83god.png',
    label: 'Beach'
  },
  {
    id: 'tech2',
    url: 'https://w.wallhaven.cc/full/yq/wallhaven-yq7lll.png',
    label: 'Moon'
  },
  {
    id: 'galaxy',
    url: 'https://w.wallhaven.cc/full/po/wallhaven-po3p19.png',
    label: 'Minecraft'
  },
  {
    id: 'gradient',
    url: 'https://w.wallhaven.cc/full/k8/wallhaven-k83g3q.jpg',
    label: 'Anime1'
  },
  {
    id: 'cyber',
    url: 'https://w.wallhaven.cc/full/zp/wallhaven-zpgrvy.jpg',
    label: 'Anime2'
  },
  {
    id: 'matrix',
    url: 'https://w.wallhaven.cc/full/vp/wallhaven-vpxemp.jpg',
    label: 'Ganyu'
  }
];

function App() {
  const [background, setBackground] = useState(backgrounds[1]); // Default to Cyberpunk background
  
  const handleBackgroundChange = (event) => {
    const selectedBackground = backgrounds.find(bg => bg.id === event.target.value);
    setBackground(selectedBackground);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          background: `url(${background.url}) no-repeat center center fixed`,
          backgroundSize: 'cover',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.8) 0%, rgba(0, 0, 0, 0.9) 100%)',
            zIndex: -1,
          },
        }}
      >
        <Header />
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 80, 
            right: 20, 
            zIndex: 10 
          }}
        >
          <FormControl 
            variant="outlined" 
            size="small"
            sx={{ 
              minWidth: 150, 
              background: 'rgba(20, 20, 20, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              border: '2px solid rgba(76, 194, 255, 0.3)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          >
            <InputLabel 
              id="background-select-label" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
              }}
            >
              Background
            </InputLabel>
            <Select
              labelId="background-select-label"
              value={background.id}
              onChange={handleBackgroundChange}
              label="Background"
              sx={{ 
                color: '#fff',
                fontWeight: 500,
                '& .MuiSelect-icon': { 
                  color: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
              {backgrounds.map((bg) => (
                <MenuItem key={bg.id} value={bg.id}>{bg.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Container 
          component="main" 
          maxWidth="md" 
          sx={{ 
            mt: 5, 
            mb: 8,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <DotTrickGenerator />
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
}

export default App; 